import { Product, Product_TallyDimension } from "gen/product/models_pb"
import { Decimal as PBDecimal } from "gen/proto/decimal/models_pb"
import { TallyRow } from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { MAX_DECIMAL_QUANTITY_PLACES } from "shared-ts/utils/constants"

import { FormErrors } from "hooks/useForm"

import { TallyFormData } from "./tally.types"

// These are some options but any denominator is technically valid on the backend. 0 means plain decimal.
export const TALLY_ENTRY_DENOMINATORS = [2, 3, 4, 8, 12, 16]

export function isTallyProduct(product?: Product): boolean {
  return (
    !!product && product.tallyDimension !== Product_TallyDimension.UNDEFINED
  )
}

export function tallyHasWidth(dimension: Product_TallyDimension): boolean {
  return (
    dimension === Product_TallyDimension.UNIFIED_LENGTH ||
    dimension === Product_TallyDimension.AREA
  )
}

export function emptyTallyRow(dimension: Product_TallyDimension): TallyRow {
  return TallyRow.create({
    quantity: Decimal.zero(),
    length: Decimal.zero(),
    ...(tallyHasWidth(dimension) ? { width: Decimal.zero() } : {}),
  })
}

export function tallyRowTotal(
  row: TallyRow,
  dimension: Product_TallyDimension
): Decimal {
  const quantity = Decimal.fromPBOrZero(row.quantity)
  const length = Decimal.fromPBOrZero(row.length)
  const width = Decimal.fromPBOrZero(row.width)
  switch (dimension) {
    case Product_TallyDimension.UNIFIED_LENGTH:
      return quantity.mult(length.add(width))
    case Product_TallyDimension.AREA:
      return quantity.mult(length).mult(width)
    default:
      return quantity.mult(length)
  }
}

export function tallyTotal(
  rows: TallyRow[],
  dimension: Product_TallyDimension
): Decimal {
  return rows
    .reduce(
      (sum, row) => sum.add(tallyRowTotal(row, dimension)),
      Decimal.zero()
    )
    .round(MAX_DECIMAL_QUANTITY_PLACES)
}

export function isTallyBlank(rows: TallyRow[]): boolean {
  return rows.every(
    (row) =>
      Decimal.fromPBOrZero(row.quantity).isZero() &&
      Decimal.fromPBOrZero(row.length).isZero() &&
      Decimal.fromPBOrZero(row.width).isZero()
  )
}

export function validateTallyRows(
  rows: TallyRow[],
  dimension: Product_TallyDimension,
  allowDecimalQuantities: boolean,
  denominator: number,
  maxTotal?: Decimal,
  uomCode?: string,
  allowNegativeQuantity = false
): FormErrors<TallyFormData> {
  const errors: FormErrors<TallyFormData> = {}
  const measurementError = (label: string, value: Decimal) => {
    if (!value.isPositive()) {
      return `${label} must be greater than 0`
    }
    if (!allowDecimalQuantities && denominator === 0 && value.places() > 0) {
      return `${label} must be a whole number for this product`
    }
    return undefined
  }
  rows.forEach((row, index) => {
    const quantity = Decimal.fromPBOrZero(row.quantity)
    const validSign = allowNegativeQuantity
      ? !quantity.isZero()
      : quantity.isPositive()
    if (!validSign || quantity.places() > 0) {
      errors[`tallyRows.${index}.quantity`] = allowNegativeQuantity
        ? "Quantity must be a whole number other than 0"
        : "Quantity must be a whole number greater than 0"
    }
    const lengthError = measurementError(
      "Length",
      Decimal.fromPBOrZero(row.length)
    )
    if (lengthError) {
      errors[`tallyRows.${index}.length`] = lengthError
    }
    const widthError = tallyHasWidth(dimension)
      ? measurementError("Width", Decimal.fromPBOrZero(row.width))
      : undefined
    if (widthError) {
      errors[`tallyRows.${index}.width`] = widthError
    }
  })
  if (
    allowNegativeQuantity &&
    rows.some((r) => Decimal.fromPBOrZero(r.quantity).isPositive()) &&
    rows.some((r) => Decimal.fromPBOrZero(r.quantity).isNegative())
  ) {
    errors.tallyRows = "Quantities must all be positive or all negative"
  }
  if (maxTotal && tallyTotal(rows, dimension).gt(maxTotal)) {
    const unit = uomCode ? ` ${uomCode}` : ""
    errors.tallyRows = `Tally total cannot exceed the ${maxTotal.toString()}${unit} remaining to return`
  }
  return errors
}

export function areTallyRowsEqual(
  first: TallyRow[],
  second: TallyRow[]
): boolean {
  if (first.length !== second.length) {
    return false
  }
  const eq = (a?: PBDecimal, b?: PBDecimal) =>
    Decimal.fromPBOrZero(a).eq(Decimal.fromPBOrZero(b))
  return first.every(
    (row, index) =>
      eq(row.quantity, second[index].quantity) &&
      eq(row.length, second[index].length) &&
      eq(row.width, second[index].width)
  )
}

// formatTallyMeasurement mirrors tallyMixedNumberDisplay in rd/txn/sale_product_v2.go.
export function formatTallyMeasurement(
  value: Decimal,
  denominator: number
): string {
  if (denominator === 0) {
    return value.round(MAX_DECIMAL_QUANTITY_PLACES).toString()
  }
  const { whole, numerator } = value.splitMixedNumber(
    denominator,
    MAX_DECIMAL_QUANTITY_PLACES
  )
  if (numerator.isZero()) {
    return `${whole}`
  }
  const fraction = `${numerator.toString()}/${denominator}`
  return whole === 0 ? fraction : `${whole} ${fraction}`
}

export function formatTallyRows(
  rows: TallyRow[],
  dimension: Product_TallyDimension,
  uomCode: string,
  denominator: number
): string {
  return rows
    .map((row) => {
      const quantity = Decimal.fromPBOrZero(row.quantity).toString()
      const length = formatTallyMeasurement(
        Decimal.fromPBOrZero(row.length),
        denominator
      )
      const width = formatTallyMeasurement(
        Decimal.fromPBOrZero(row.width),
        denominator
      )
      const part =
        dimension === Product_TallyDimension.UNIFIED_LENGTH
          ? `${quantity} × (${length} + ${width})`
          : dimension === Product_TallyDimension.AREA
            ? `${quantity} × ${length} × ${width}`
            : `${quantity} × ${length}`
      return uomCode ? `${part} ${uomCode}` : part
    })
    .join(", ")
}

export function tallySubmissionArgs({
  tallyRows,
  tallyDimension,
  tallyEntryDenominator,
}: TallyFormData) {
  if (isTallyBlank(tallyRows)) {
    return {
      tallyRows: [],
      tallyDimension: Product_TallyDimension.UNDEFINED,
      tallyEntryDenominator: 0,
    }
  }
  return {
    tallyRows,
    tallyDimension,
    tallyEntryDenominator,
    quantity: tallyTotal(tallyRows, tallyDimension),
  }
}
